import MUIContainer from '@/components/MUI-Components/MUI-Container';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import React, {memo} from 'react';
import parse from 'html-react-parser';
import DisplayImage from '../display-image';
import {
    contentMaxWidth,
    colors,
    shadows,
    borderRadius,
    fontSize,
    fontWeight,
    lineHeightRatio,
    transition,
    gradients
} from '@/styles/tokens';
import {StaticImageData} from 'next/image';

const sectionHeadingSx = {mb: 5};

const platformGridSx = (perRow: number) => ({
    display: 'grid',
    gridTemplateColumns: {xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)', md: `repeat(${perRow}, 1fr)`},
    gap: {xs: 2, md: 3},
    width: '100%',
    maxWidth: contentMaxWidth.standard,
    mx: 'auto',
    mb: 8
});

/** Roomier tiles pair with a larger logo, so the padding follows the icon size. */
const platformLogoPaddingSx = (iconSize: number) => (iconSize >= 56 ? {px: 1.5, py: 2} : {px: 1, py: 2});

const platformLogoSx = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 2,
    border: `1px solid ${colors.neutral.borderLight}`,
    borderRadius: 2,
    background: colors.background.default,
    boxShadow: shadows.platformCard,
    transition: `border-color ${transition.base}, background ${transition.base}, transform ${transition.base}`,
    '&:hover': {
        borderColor: colors.primary.main,
        background: colors.background.subtle,
        transform: 'scale(1.05)'
    },
    '@media (prefers-reduced-motion: reduce)': {
        transition: 'none',
        '&:hover': {transform: 'none'}
    }
};

const featureCardSx = {
    bgcolor: colors.background.default,
    border: `1px solid ${colors.neutral.borderLight}`,
    borderRadius: 3,
    p: {xs: 1.5, md: 2},
    boxShadow: shadows.lg,
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: 1
};

const iconBoxSx = {
    background: gradients.primaryActive,
    width: 48,
    height: 48,
    minWidth: 48,
    borderRadius: 2,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    mb: 1.5,
    flexShrink: 0,
    p: 2
};

const cardTitleSx = {
    fontSize: fontSize[20],
    pt: 0,
    pb: 1
};

const cardBodyTextSx = {
    fontSize: fontSize[16],
    color: colors.text.primary
};

const badgeSx = {
    display: 'inline-block',
    px: 1.5,
    py: 0.5,
    borderRadius: borderRadius.full,
    border: `1px solid ${colors.neutral.borderLight}`,
    bgcolor: colors.background.tag,
    fontSize: fontSize[14],
    color: colors.primary.deep,
    fontWeight: fontWeight.semibold,
    lineHeight: lineHeightRatio.snug
};

const platformLabelSx = {
    fontSize: {xs: fontSize[12], sm: fontSize[14]},
    fontWeight: fontWeight.semibold,
    color: colors.text.primary,
    textAlign: 'center',
    lineHeight: lineHeightRatio.snug
};

interface PlatformItem {
    label: string;
    icon?: string | StaticImageData;
}

interface IntegrationCard {
    icon?: string | StaticImageData;
    title: string;
    body: string;
    chips: string[];
}

interface ChatbotIntegrationsProps {
    heading: string;
    subheading?: string;
    platforms: PlatformItem[];
    contentData: IntegrationCard[];
    /** Platform badges per row on desktop. Defaults to 6. */
    platformsPerRow?: number;
    /** Platform logo size in px. Defaults to 40. */
    platformIconSize?: number;
}

const ChatbotIntegrations = ({
    heading,
    subheading,
    platforms,
    contentData,
    platformsPerRow = 6,
    platformIconSize = 40
}: ChatbotIntegrationsProps) => {
    return (
        <Box>
            <MUIContainer>
                <Box textAlign="center" maxWidth={1024} margin="0 auto" sx={sectionHeadingSx}>
                    <Typography variant="h1" component="h2" mb={3} fontSize={{xs: 35, sm: 48}} className="font-headers">
                        {heading ? parse(heading) : ''}
                    </Typography>
                    {subheading && (
                        <Typography variant="subtitle1" component="p" className="font-regular-text">
                            {parse(subheading)}
                        </Typography>
                    )}
                </Box>

                {platforms?.length > 0 && (
                    <Box sx={platformGridSx(platformsPerRow)}>
                        {platforms.map((platform) => (
                            <Box key={platform.label} sx={[platformLogoSx, platformLogoPaddingSx(platformIconSize)]}>
                                {platform.icon && (
                                    <Box
                                        width={platformIconSize}
                                        height={platformIconSize}
                                        display="flex"
                                        alignItems="center"
                                        justifyContent="center"
                                    >
                                        <DisplayImage
                                            src={platform.icon}
                                            alt={platform.label}
                                            width={platformIconSize}
                                            height={platformIconSize}
                                        />
                                    </Box>
                                )}
                                <Typography className="font-regular-text" sx={platformLabelSx}>
                                    {platform.label}
                                </Typography>
                            </Box>
                        ))}
                    </Box>
                )}

                <Grid container spacing={3}>
                    {contentData?.map((card) => (
                        <Grid item xs={12} sm={6} md={4} key={card.title}>
                            <Box sx={featureCardSx}>
                                <Box sx={iconBoxSx}>
                                    {card.icon && (
                                        <DisplayImage
                                            src={card.icon}
                                            alt={card.title ?? ''}
                                            width={36}
                                            height={36}
                                            style={{filter: 'brightness(0) invert(1)', padding: 4}}
                                        />
                                    )}
                                </Box>
                                <Typography variant="h3" component="h3" sx={cardTitleSx} className="font-headers">
                                    {card.title}
                                </Typography>
                                <Typography variant="body1" className="font-regular-text remove-underline" sx={cardBodyTextSx}>
                                    {card.body}
                                </Typography>
                                {card.chips?.length > 0 && (
                                    <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1.5}}>
                                        {card.chips.map((chip) => (
                                            <Box key={chip} component="span" sx={badgeSx}>
                                                {chip}
                                            </Box>
                                        ))}
                                    </Box>
                                )}
                            </Box>
                        </Grid>
                    ))}
                </Grid>
            </MUIContainer>
        </Box>
    );
};

export default memo(ChatbotIntegrations);
